import { Color } from '@coast/schemas';
import { ClassValue } from 'clsx';
import { isNil } from 'lodash';
import { Pill } from '@shared/components/pill';
import { CombinedTagsField } from '@shared/data/entity/fields/CombinedTagsField';
import { TagOption, useCombinedTagOptions } from '@shared/util/hooks/useWorkflowTagOptions';
import { notNil } from '@shared/util/notNil';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import styles from './CombinedTagsCollectionViewField.module.css';

const MAX_TAGS_TO_SHOW = 10;

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: CombinedTagsField;
}

/** Default and compact: a pill for each tag, up to ten, and a "+N". Inline: the tags' labels. */
export function CombinedTagsCollectionViewField({ field, variant, className }: Props) {
  const { options } = useCombinedTagOptions(field.componentIds);

  if (field.isEmpty || isNil(options)) {
    return null;
  }

  const tags: (TagOption | undefined)[] = [];

  field.tags.forEach((value, key) => {
    value.forEach(v => tags.push(options.find(o => o.fieldName === key && o.value === v)));
  });

  if (variant === 'inline') {
    return (
      <CollectionViewFieldContainer
        fieldType={field.fieldType}
        variant={variant}
        className={className}
      >
        {tags
          .filter(notNil)
          .map(option => option.label)
          .join(', ')}
      </CollectionViewFieldContainer>
    );
  }

  const tagsToShow = tags.slice(0, MAX_TAGS_TO_SHOW);
  const remainingTagCount = tags.length - MAX_TAGS_TO_SHOW;

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.combinedTagsCollectionViewField, className]}
    >
      {tagsToShow.map(option => {
        if (isNil(option)) {
          return null;
        }
        return (
          <Pill
            key={option.id}
            backgroundColor={option.backgroundColor as Color}
            round
            sections={[option.label]}
          />
        );
      })}
      {remainingTagCount > 0 && (
        <Pill backgroundColor="transparent" sections={[`+${remainingTagCount}`]} round noBorder />
      )}
    </CollectionViewFieldContainer>
  );
}
